import {
  FormControlLabel,
  type FormControlLabelProps,
  Radio,
  RadioGroup,
  type RadioGroupProps,
  type RadioProps,
  styled,
} from "@mui/material"
import type { ChangeEvent } from "react"
import type { ReferentialCode } from "../../../types"

export interface SquircleSelectorProps<T extends string | number> {
  value: T
  referential: ReferentialCode<T>[]
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  slotProps?: {
    radioGroupSx?: RadioGroupProps["sx"]
    formControlLabelSx?: FormControlLabelProps["sx"]
    radioSx?: RadioProps["sx"]
  }
}

export const SquircleSelector = <T extends string | number>({
  value,
  referential,
  onChange,
  slotProps,
}: SquircleSelectorProps<T>) => {
  return (
    <OptionGroup value={value} onChange={onChange} sx={slotProps?.radioGroupSx}>
      {referential.map(({ label, code }) => (
        <Option
          key={label}
          value={code}
          label={label}
          isChecked={value === code}
          control={<InvisibleRadio sx={slotProps?.radioSx} />}
          sx={slotProps?.formControlLabelSx}
        />
      ))}
    </OptionGroup>
  )
}

const OptionGroup = styled(RadioGroup)(({ theme: { palette, spacing } }) => ({
  backgroundColor: palette.common.ghostBlue,
  borderRadius: spacing(0.5),
  padding: spacing(0.25),
  cornerShape: "squircle",
  width: "fit-content",
}))
OptionGroup.defaultProps = {
  row: true,
}

const Option = styled(FormControlLabel, {
  shouldForwardProp: (prop) => !(prop === "isChecked"),
})<{ isChecked?: boolean }>(
  ({ theme: { typography, spacing, palette }, isChecked }) => ({
    margin: 0,
    fontSize: typography.pxToRem(14),
    borderRadius: spacing(0.25),
    padding: `${spacing(0.5)} ${spacing(2)}`,
    color: palette.primary.main,
    backgroundColor: isChecked ? "white" : "transparent",
    cornerShape: "inherit",
  }),
)
const InvisibleRadio = styled(Radio)(() => ({
  visibility: "hidden",
  width: 0,
  height: 0,
  padding: 0,
}))
